// Offline-logout durable stash for un-uploaded capture work (#809 Tier-3).
//
// WHY THIS EXISTS. Logout runs `disconnectAndClear({ clearLocal: true })`, which
// wipes the synced mirror AND the ps_crud upload queue AND the
// `orphaned_capture_items` quarantine — un-uploaded contractual counts that live
// ONLY on this device. Online, those counts are preserved before the clear
// (rescue-held-captures.ts). OFFLINE, nothing can reach the server, so the counts
// would be destroyed by the clear. This module is the offline net: it stashes the
// un-uploaded counts in durable on-device storage that SURVIVES the clear, and an
// on-login re-inject restores them once the device is back online.
//
// WHERE THE RE-INJECTED COUNTS GO (the #809 follow-up fix). The held work is two
// distinct dimensions and the re-inject routes them DIFFERENTLY:
//   - queueItems      — ps_crud-QUEUED counts: un-uploaded but perfectly SYNCABLE.
//     These are re-INSERTed back into the LIVE capture path (capture_items →
//     ps_crud → connector upload), so on the user's next online login they sync
//     like any other offline capture: the upload queue DRAINS ON THE DEVICE and
//     the counts become live `capture_items` (visible in the dashboard / CSV).
//     The earlier Tier-3 shipped these to the recovery inbox instead, where they
//     sat un-placed and INVISIBLE to the surveyor — "I logged back in and my
//     counts never came back". Re-queuing is the fix.
//   - quarantineItems — `orphaned_capture_items`: counts the connector already
//     FAILED to upload (404 / invalid_argument). They cannot sync as-is, so they
//     still ship to the isolated server recovery inbox (POST /capture/recovery-dump)
//     for admin Phase-2 placement — exactly the established quarantine path.
// Re-queuing is SAFE because the connector is hardened: a re-queued count that is
// still structurally un-syncable (e.g. its room was deleted server-side) is
// re-quarantined by the connector rather than wedging the queue, and the live
// AutoRescueHeldCaptures then ships it to the inbox. So the fix is strictly ≥ the
// old behaviour: good counts sync, broken ones still reach the inbox.
//
// SHARED-DEVICE SAFETY — the load-bearing invariant. Field staff share devices,
// and held counts carry no capturing-user id of their own. The whole reason the
// logout clear exists is so user A's data is never visible/attributable to user
// B on the same device. So the stash is **keyed by the LOGGING-OUT user's id**
// (known from auth at logout) and re-injected ONLY when that SAME user logs back
// in. A different user (B) logging in never reads, ships, or re-queues A's stash —
// the re-queue runs under A's own session on A's next login, and the recovery POST
// is attributed to the authed caller, so re-injecting A's stash under B would
// MISATTRIBUTE A's counts to B; the per-user key prevents that. This is why the
// clear still runs (synced-mirror isolation is preserved); only A's own un-synced
// work is retained, scoped to A.
//
// STORAGE SEAM. Pure over a minimal async key-value `StashStore` so it is unit-
// testable without IndexedDB (the jsdom env lacks it) and so the persistence
// engine is swappable. Production wires the IndexedDB-backed store
// (indexed-db-stash-store.ts) — IndexedDB, NOT a PowerSync local-only table,
// because `clearLocal: true` wipes those too, and NOT localStorage, because a
// 7,000+ count backlog (Sentry 1P) can exceed its ~5 MB quota.
//
// RESIDUALS (documented, accepted — preserve-over-isolation for contractual
// counts): (1) A's un-synced counts sit in A-scoped storage on the device until A
// is next online; if A never returns to this device (or it is factory-reset) they
// strand. (2) Plaintext at rest (asset quantities + room/session ids — not
// personal data). Encryption-at-rest is an available hardening (a key strategy is
// the open question) and is deliberately NOT shipped here.

import {
  POST_CHUNK,
  type DumpPoster,
  type HeldWorkBundle,
  type RecoveryDumpItem,
  type RescueResult,
} from './rescue-held-captures'

/** Minimal async key-value store the stash persists through. Implemented by an
 *  IndexedDB-backed store in production (indexed-db-stash-store.ts); tests inject
 *  an in-memory fake. `get` resolves `undefined` for an absent key. */
export interface StashStore {
  get(key: string): Promise<unknown>
  /**
   * Atomically read-modify-write `key` in a SINGLE transaction: `updater` receives
   * the current value (or `undefined` for an absent key) and returns the next value,
   * or `undefined` to DELETE the key. The get→put/delete MUST run in one transaction
   * so two concurrent same-key updates (e.g. two PWA tabs of the same user logging
   * out offline, or a stash racing a re-inject removal) SERIALIZE as compare-and-swap
   * rather than lost-update — the difference between keeping and silently dropping a
   * contractual count (Codex P2). The `updater` must be SYNCHRONOUS so the
   * transaction stays open across the get→put.
   */
  update(key: string, updater: (current: unknown) => unknown): Promise<void>
  delete(key: string): Promise<void>
}

/** One user's stashed un-uploaded counts, split by re-inject dimension (see the
 *  module header + HeldWorkBundle). `userId` is the logging-out user — the
 *  re-inject gate compares it to the next authed user so A's work never restores
 *  under B. `stashedAt` is the device clock at logout (forensic only). */
export interface StashedHeldWork {
  readonly userId: string
  readonly queueItems: RecoveryDumpItem[]
  readonly quarantineItems: RecoveryDumpItem[]
  readonly stashedAt: string
}

/** A re-inject sink for ONE dimension: hands a bounded chunk to its destination
 *  (re-queue into the live capture path, or POST to the recovery inbox) and
 *  resolves with the number it successfully handled. THROWING leaves that chunk in
 *  the stash to retry on the next trigger (NO-LOSS) — never swallow. */
export type HeldWorkSink = (items: RecoveryDumpItem[]) => Promise<number>

// One key per user so concurrent users on a shared device never collide and a
// re-inject only ever touches the matching user's slot.
function keyFor(userId: string): string {
  return `held-work-stash:${userId}`
}

/** The two dimension slots the re-inject drains independently. */
type Slot = 'queueItems' | 'quarantineItems'

const EMPTY_BUNDLE: HeldWorkBundle = { queueItems: [], quarantineItems: [] }

/** The stored-slot shape both stash formats share, with NAMED optional props so the
 *  reads below are real property accesses (the gate anchors on `.userId`), not
 *  unchecked index-signature lookups. `items` is the legacy #827 flat union. */
interface StashSlotShape {
  userId: unknown
  queueItems?: unknown
  quarantineItems?: unknown
  items?: unknown
  stashedAt?: unknown
}

function isStashSlot(value: unknown): value is StashSlotShape {
  return typeof value === 'object' && value !== null && 'userId' in value
}

function asItems(value: unknown): RecoveryDumpItem[] {
  return Array.isArray(value) ? (value as RecoveryDumpItem[]) : []
}

/**
 * The held-work bundle stored for `userId` — empty if the slot is absent,
 * malformed, or ANOTHER user's (never restore one user's stash as another's —
 * the load-bearing NO-LEAK re-check). The single parse used by both the read and
 * the in-transaction updaters.
 *
 * BACK-COMPAT: the first Tier-3 build stored a single flat `items` union
 * ([...quarantined, ...queued]) and shipped ALL of it to the recovery inbox. A
 * device that stashed under that build before this fix shipped must not lose those
 * counts, so a legacy `{ items }` slot is read as ALL-quarantine — i.e. it ships
 * to the inbox exactly as that build did (no regression, no loss). New stashes use
 * the split `{ queueItems, quarantineItems }` shape and get the re-queue treatment.
 */
function ownedBundle(current: unknown, userId: string): HeldWorkBundle {
  if (!isStashSlot(current)) return EMPTY_BUNDLE
  // NO-LEAK identity re-check: only ever return a slot whose stored userId matches
  // the requesting user, failing safe to empty — a mis-keyed / tampered slot must
  // never restore another user's counts on a shared device.
  if (current.userId !== userId) return EMPTY_BUNDLE
  if (current.queueItems !== undefined || current.quarantineItems !== undefined) {
    return {
      queueItems: asItems(current.queueItems),
      quarantineItems: asItems(current.quarantineItems),
    }
  }
  // Legacy flat `{ items }` slot — preserve the old inbox-only behaviour (no loss).
  if (current.items !== undefined) {
    return { queueItems: [], quarantineItems: asItems(current.items) }
  }
  return EMPTY_BUNDLE
}

function bundleIsEmpty(bundle: HeldWorkBundle): boolean {
  return bundle.queueItems.length === 0 && bundle.quarantineItems.length === 0
}

function stashedAtOf(current: unknown): string {
  return isStashSlot(current) && typeof current.stashedAt === 'string'
    ? current.stashedAt
    : ''
}

/** Dedupe-merge `incoming` into `existing` by `originalItemId`, LATEST winning —
 *  the capture_items id is absolute-latest-wins, mirroring the server inbox upsert
 *  and the connector's same-id LWW, so a count stashed twice never duplicates and
 *  the freshest quantity survives. */
function mergeById(
  existing: RecoveryDumpItem[],
  incoming: RecoveryDumpItem[],
): RecoveryDumpItem[] {
  const byId = new Map<string, RecoveryDumpItem>()
  for (const item of existing) byId.set(item.originalItemId, item)
  for (const item of incoming) byId.set(item.originalItemId, item)
  return [...byId.values()]
}

/** Pure CAS updater: merge `bundle` into `current`'s owned bundle (per dimension)
 *  and return the new slot. Runs INSIDE store.update's transaction. */
function mergeBundleUpdater(
  current: unknown,
  userId: string,
  bundle: HeldWorkBundle,
  stampedAt: string,
): StashedHeldWork {
  const existing = ownedBundle(current, userId)
  return {
    userId,
    queueItems: mergeById(existing.queueItems, bundle.queueItems),
    quarantineItems: mergeById(existing.quarantineItems, bundle.quarantineItems),
    stashedAt: stampedAt,
  }
}

/** Do two stash snapshots of one slot carry the SAME contractual value? Compares the
 *  fields a concurrent re-stash would change (quantity / zero-confirmation / notes). */
function sameStashValue(a: RecoveryDumpItem, b: RecoveryDumpItem): boolean {
  return (
    a.quantity === b.quantity &&
    a.isZeroConfirmed === b.isZeroConfirmed &&
    a.notes === b.notes
  )
}

/** Pure CAS updater: remove the just-shipped `handled` items from ONE dimension slot of
 *  `current`'s owned bundle, PRESERVING the other slot, any items a concurrent stash
 *  merged in under a NEW id, AND any handled id whose stored VALUE CHANGED since it was
 *  shipped — a concurrent same-user tab may have merged a NEWER quantity mid-ship
 *  (mergeById, latest-wins); removing by id alone would silently drop that newer value
 *  (Codex P2). Keep it so it re-ships on the next drain (the inbox upsert / re-queue then
 *  converges, latest-wins). Returns `undefined` (→ delete the key) when NOTHING remains in
 *  EITHER slot. Runs INSIDE store.update. */
function removeHandledUpdater(
  current: unknown,
  userId: string,
  handled: readonly RecoveryDumpItem[],
  slot: Slot,
): StashedHeldWork | undefined {
  const bundle = ownedBundle(current, userId)
  const shippedById = new Map(handled.map((item) => [item.originalItemId, item]))
  const next: HeldWorkBundle = {
    queueItems: bundle.queueItems,
    quarantineItems: bundle.quarantineItems,
    [slot]: bundle[slot].filter((item) => {
      const shipped = shippedById.get(item.originalItemId)
      return shipped === undefined || !sameStashValue(shipped, item)
    }),
  }
  if (bundleIsEmpty(next)) return undefined
  return {
    userId,
    queueItems: next.queueItems,
    quarantineItems: next.quarantineItems,
    stashedAt: stashedAtOf(current),
  }
}

/**
 * Stash `bundle` as un-uploaded held work for `userId`, MERGING with anything
 * already stashed (a prior offline logout, or a re-stash before the re-inject
 * ran). Dedupe by `originalItemId` with the LATEST occurrence winning, per
 * dimension. The read-merge-write is a SINGLE atomic store.update (CAS), so two
 * same-user tabs logging out offline at once can't lost-update each other's counts
 * (Codex P2). A no-op (no write) when there is nothing to stash.
 */
export async function stashHeldWork(
  store: StashStore,
  userId: string,
  bundle: HeldWorkBundle,
  stampedAt: string,
): Promise<void> {
  // Shared-device safety: never write an unkeyed slot. The sole caller
  // (proceedLogout) already guards userId !== undefined and a real Entra userID is
  // never empty, but a bare `held-work-stash:` key would be readable by any future
  // empty-id principal — refuse it at the seam rather than trust every caller.
  if (!userId) return
  if (bundleIsEmpty(bundle)) return
  await store.update(keyFor(userId), (current) =>
    mergeBundleUpdater(current, userId, bundle, stampedAt),
  )
}

/** Read the un-uploaded held work stashed for `userId` (empty bundle if none, or
 *  if the stored value is malformed / another user's — a corrupt slot must never
 *  crash the re-inject, only yield nothing to restore). */
export async function readStashedHeldWork(
  store: StashStore,
  userId: string,
): Promise<HeldWorkBundle> {
  return ownedBundle(await store.get(keyFor(userId)), userId)
}

/** Drop `userId`'s whole stash slot. Retained as a public StashStore-layer
 *  primitive (with its own behavioural test): the re-inject path no longer calls it
 *  — it removes handled ids per chunk via `store.update` so a concurrent same-user
 *  merge survives (the empty slot self-deletes via removeHandledUpdater → undefined)
 *  — but a whole-slot drop is the correct operation for any future "discard this
 *  user's stash" caller, so it stays. */
export async function clearStashedHeldWork(
  store: StashStore,
  userId: string,
): Promise<void> {
  await store.delete(keyFor(userId))
}

/** Drain ONE dimension: hand each bounded chunk to `sink`, then atomically REMOVE
 *  just that chunk's ids from `slot` — NEVER the whole key. So a chunk is removed
 *  ONLY after its handling is CONFIRMED (a throw mid-run leaves every un-handled
 *  chunk in the stash to retry next login), a concurrent same-user stash that
 *  merges NEW counts mid-re-inject is preserved (we delete handled ids, not the
 *  key — Codex P2), and a backlog larger than the rate window makes PROGRESS (a
 *  later-chunk throw keeps the earlier chunks' removals). Resolves with the number
 *  the sink handled. */
async function drainDimension(
  store: StashStore,
  userId: string,
  items: RecoveryDumpItem[],
  sink: HeldWorkSink,
  slot: Slot,
): Promise<number> {
  let handled = 0
  for (let i = 0; i < items.length; i += POST_CHUNK) {
    const chunk = items.slice(i, i + POST_CHUNK)
    // Hand off FIRST — remove only after this resolves. Removing before the sink
    // confirms would drop the only copy if it then fails (#809 NO-LOSS).
    handled += await sink(chunk)
    await store.update(keyFor(userId), (current) =>
      removeHandledUpdater(current, userId, chunk, slot),
    )
  }
  return handled
}

/**
 * On-login re-inject of `userId`'s offline-logout stash, routing each dimension to
 * its correct destination (see the module header + HeldWorkBundle):
 *   • queueItems      → `requeue` — re-INSERT into the LIVE capture path so they
 *     re-enter ps_crud and the upload queue drains visibly on the device. This is
 *     the fix: the surveyor's offline counts come back and sync, instead of sitting
 *     un-placed and invisible in the recovery inbox.
 *   • quarantineItems → `post`    — ship to the isolated recovery inbox for admin
 *     Phase-2 placement (the established quarantine path).
 * Reads ONLY that user's slot (shared-device safety). Each chunk is removed from
 * the stash ONLY after its sink confirms (drainDimension; NO-LOSS), and the inbox
 * upserts on (user, original_item_id) / the re-queue is idempotent on the
 * capture_items id, so a retry never duplicates. Pure over the `StashStore`,
 * `DumpPoster`, and `HeldWorkSink` seams (unit-testable without IndexedDB, the
 * network, or PowerSync). An empty stash is a no-op success. Throws only if a sink
 * throws (the caller surfaces/swallows), leaving the un-handled remainder to retry.
 */
export async function reinjectStash(
  store: StashStore,
  post: DumpPoster,
  requeue: HeldWorkSink,
  userId: string,
): Promise<RescueResult> {
  const { queueItems, quarantineItems } = await readStashedHeldWork(store, userId)
  if (queueItems.length === 0 && quarantineItems.length === 0) {
    return { found: 0, sent: 0 }
  }
  // Adapt the inbox poster (returns the server's stored-count) to the dimension
  // sink shape so both dimensions drain through the identical NO-LOSS machinery.
  const postSink: HeldWorkSink = (items) =>
    post(items).then((res) => res.stored)

  let sent = 0
  // Syncable dimension FIRST: re-queue into the live capture path so the upload
  // queue drains. The inbox dimension second.
  sent += await drainDimension(store, userId, queueItems, requeue, 'queueItems')
  sent += await drainDimension(
    store,
    userId,
    quarantineItems,
    postSink,
    'quarantineItems',
  )
  return { found: queueItems.length + quarantineItems.length, sent }
}
